import { useCallback, useMemo } from "react";

import { Button, Col, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import Input from "@/common/components/form/Input.tsx";
import { phoneRule } from "@/common/formValidation/index.ts";

import { createServiceTenant, updateServiceTenant } from "../../services";

export type ActionTenant = "add" | "edit";

export type TenantForm = {
  firstname?: string;
  lastname?: string;
  phone?: string;
  email?: string;
  newAddress?: string;
};

export const TenantFormModal = ({
  tenantId,
  serviceId,
  defaultValues,
  show,
  action,
  onHide,
}: {
  tenantId?: string;
  serviceId: string;
  defaultValues?: TenantForm;
  show: boolean;
  action: ActionTenant;
  onHide: () => void;
}) => {
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset } = useForm<TenantForm>();

  useMemo(() => {
    reset(
      defaultValues ?? {
        firstname: "",
        lastname: "",
        phone: "",
        email: "",
        newAddress: "",
      }
    );
  }, [reset, defaultValues]);

  const invalidateData = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["service", serviceId],
    });
  };

  const mutationUpdatePerson = useMutation({
    mutationFn: (tenant: TenantForm) =>
      action === "add"
        ? createServiceTenant(serviceId, tenant)
        : updateServiceTenant(serviceId, tenantId as string, tenant),
    onError: (error) => {
      console.error(error);
      toast.error(
        action === "add"
          ? "Une erreur est survenue lors de l'ajout de l'occupant"
          : "Une erreur est survenue lors de la mise à jour de l'occupant"
      );
      handleClose();
    },
    onSuccess: async () => {
      toast.success(
        action === "add"
          ? "Le locataire a été ajouté avec succès"
          : "Le locataire a été mis à jour avec succès"
      );
      await invalidateData();
      handleClose();
    },
  });

  const onSubmit = (data: TenantForm) => {
    mutationUpdatePerson.mutate(data);
  };

  const handleClose = () => {
    reset({
      firstname: "",
      lastname: "",
      phone: "",
      email: "",
      newAddress: "",
    });
    onHide();
  };

  const modalTitle = useCallback((): string => {
    switch (action) {
      case "add":
        return "Ajouter un occupant";
      case "edit":
        return "Modifier un occupant";
      default:
        return "";
    }
  }, [action]);

  return (
    <Modal size="lg" show={show} onHide={handleClose} centered>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle()}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Col>
            <Row>
              <Col md={6}>
                <Input
                  label={"Nom :"}
                  control={control}
                  name={"lastname"}
                  rules={{
                    required: "Veuillez renseigner un nom",
                  }}
                />
              </Col>
              <Col md={6}>
                <Input
                  label={"Prénom :"}
                  control={control}
                  name={"firstname"}
                  rules={{
                    required: "Veuillez renseigner un prénom",
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Input
                  label={"Numéro de téléphone :"}
                  control={control}
                  name={"phone"}
                  rules={{
                    ...phoneRule,
                    required: "Veuillez renseigner un numéro de téléphone",
                  }}
                />
              </Col>
              <Col md={6}>
                <Input
                  label={"Adresse email :"}
                  control={control}
                  name={"email"}
                  rules={{
                    required: "Veuillez renseigner une adresse email",
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Retour
          </Button>

          <Button type={"submit"}>
            {action === "edit" ? (
              <>
                <span className={"me-2"}>Modifier</span>
                <i className="bi bi-pencil" />
              </>
            ) : null}
            {action === "add" ? (
              <>
                <span className={"me-2"}>Ajouter</span>
                <i className="bi bi-plus" />
              </>
            ) : null}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
